/**
 * Web Vitals telemetry — reports Core Web Vitals (LCP, INP, CLS, FCP, TTFB)
 * from the `web-vitals` library to the backend as `analytics_events` rows
 * with `event: 'web_vitals'`. Each metric is fired independently as it
 * finalizes (LCP on user interaction/tab-hide, CLS on tab-hide, etc.) so
 * we don't lose data if the user closes the tab mid-session.
 *
 * We store LCP/INP/FCP/TTFB in milliseconds, CLS as a dimensionless score
 * (typically 0-1). The `rating` field is the library's own thresholding —
 * "good" / "needs-improvement" / "poor" — which lets the Admin Visitors
 * page bucket P75 into a green/amber/red pill without recomputing thresholds
 * on the client.
 *
 * anon_id is looked up from the same localStorage key as VisitorTracker so
 * a single visitor's page-view + vitals events can be joined server-side.
 * UTMs are attached too so we can correlate "instagram-ad-A" with an LCP
 * regression before shipping the ad.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ANON_KEY = "dhruva_anon_id";
const UTM_KEY = "dhruva_utm";

function readAnonId() {
  try { return localStorage.getItem(ANON_KEY); } catch { return null; }
}
function readUtms() {
  try { return JSON.parse(localStorage.getItem(UTM_KEY) || "{}"); } catch { return {}; }
}

function post(metric) {
  const anon = readAnonId();
  if (!anon) return; // VisitorTracker hasn't run yet — skip; will fire next route
  const body = {
    event: "web_vitals",
    anon_id: anon,
    path: window.location.pathname,
    referrer: document.referrer || null,
    meta: {
      metric: metric.name,          // LCP / INP / CLS / FCP / TTFB
      value: metric.value,          // ms for time metrics, unitless for CLS
      rating: metric.rating,        // "good" | "needs-improvement" | "poor"
      delta: metric.delta,
      id: metric.id,
      navigation_type: metric.navigationType,
    },
    ...readUtms(),
  };
  // Prefer sendBeacon — survives tab close, doesn't block navigation.
  const url = `${API}/public/track`;
  const payload = JSON.stringify(body);
  if (navigator.sendBeacon) {
    try {
      const blob = new Blob([payload], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    } catch { /* fall through to fetch */ }
  }
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    keepalive: true,
  }).catch(() => {});
}

export default function reportWebVitals() {
  // Dynamic import so `web-vitals` lives in its own tiny chunk and doesn't
  // slow down the main bundle. Runs after the initial paint anyway.
  import("web-vitals").then(({ onLCP, onINP, onCLS, onFCP, onTTFB }) => {
    onLCP(post);
    onINP(post);
    onCLS(post);
    onFCP(post);
    onTTFB(post);
  }).catch(() => {});
}
